import { z } from 'zod';

/** Bumped on any breaking change; stale clients are told to reload. */
export const REALTIME_PROTOCOL_VERSION = 1;
export const REALTIME_PATH = '/api/v1/ws';
export const MAX_CLIENT_FRAME_BYTES = 1024;

export const matchPhaseSchema = z.enum([
  'WAITING_FOR_PLAYERS',
  'READY_CHECK',
  'COUNTRY_VOTE_OPEN',
  'RUNOFF_VOTE_OPEN',
  'VOTE_RESOLVING',
  'COUNTRY_SELECTED',
  'EPISODE_INTRO',
  'IN_MATCH',
  'MATCH_COMPLETE',
  'CANCELLED',
]);

const teamSchema = z.enum(['plot', 'detail']);
export type Team = z.infer<typeof teamSchema>;
const isoDate = z.iso.datetime({ offset: true });
const countryIdSchema = z.string().regex(/^[a-z][a-z0-9-]{1,39}$/);

export const lobbyCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-HJ-NP-Z2-9]{6}$/, 'Lobby codes are 6 letters and numbers.');

// ---- Requests ----

export const lobbyVisibilitySchema = z.enum(['private', 'public']);
export type LobbyVisibility = z.infer<typeof lobbyVisibilitySchema>;

/** 8 seats, or 6 / 4 for private lobbies only. */
export const maxPlayersSchema = z.union([z.literal(4), z.literal(6), z.literal(8)]);
export type MaxPlayers = z.infer<typeof maxPlayersSchema>;

export const createMatchRequestSchema = z
  .object({
    visibility: lobbyVisibilitySchema.default('private'),
    maxPlayers: maxPlayersSchema.default(8),
  })
  .strict()
  .refine((v) => v.visibility === 'private' || v.maxPlayers === 8, {
    path: ['maxPlayers'],
    message: 'Public matches always have 8 players.',
  });

/** Host-only, while waiting: shrink or grow a private table (never below who is seated). */
export const setLobbySizeRequestSchema = z.object({ maxPlayers: maxPlayersSchema }).strict();
export const joinMatchRequestSchema = z.object({ code: lobbyCodeSchema }).strict();
/** Matchmaking takes no input: the server picks the lobby. */
export const quickJoinRequestSchema = z.object({}).strict();
export const setReadyRequestSchema = z.object({ ready: z.boolean() }).strict();
export const submitVoteRequestSchema = z
  .object({
    roundId: z.uuid(),
    countryId: countryIdSchema,
    /** Client-generated per vote attempt; retries of the same attempt reuse it. */
    requestId: z.uuid(),
  })
  .strict();
export const matchIdParamSchema = z.object({ matchId: z.uuid() }).strict();

export type SubmitVoteRequest = z.infer<typeof submitVoteRequestSchema>;

// ---- Per-player view (the only match data a client ever receives) ----

export const playerViewSchema = z.object({
  slot: z.number().int().min(1).max(8),
  codename: z.string(),
  isYou: z.boolean(),
  isHost: z.boolean(),
  ready: z.boolean(),
  connected: z.boolean(),
  status: z.enum(['active', 'left', 'removed', 'account_deleted']),
  /** Only revealed for yourself and your teammates. */
  team: teamSchema.nullable(),
});

export const voteRoundViewSchema = z.object({
  roundId: z.uuid(),
  kind: z.enum(['first', 'runoff']),
  candidates: z.array(countryIdSchema),
  deadline: isoDate,
  closed: z.boolean(),
  /** Accepted votes only. */
  counts: z.record(z.string(), z.number().int().min(0)),
  markers: z.array(z.object({ slot: z.number().int(), countryId: countryIdSchema })),
  yourVote: countryIdSchema.nullable(),
  eligible: z.boolean(),
});

export const resolutionViewSchema = z.object({
  countryId: countryIdSchema,
  reason: z.enum(['PLURALITY', 'RUNOFF_PLURALITY', 'RUNOFF_RANDOM_TIEBREAK', 'NO_VOTES_RANDOM']),
  counts: z.record(z.string(), z.number().int().min(0)),
  tiedCountryIds: z.array(countryIdSchema).optional(),
});

const expressionSchema = z.enum(['neutral', 'smug', 'alarmed', 'scheming']);
const hex = z.string().regex(/^#[0-9a-f]{6}$/i);

export const castMemberViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  title: z.string(),
  bio: z.string(),
  look: z.object({ skin: hex, hair: hex, suit: hex, accent: hex }),
  /** Portrait rig parts; mirrors the content build schema. */
  build: z.object({
    species: z.enum(['person', 'dog', 'cat', 'mascot']),
    face: z.enum(['round', 'long', 'square']),
    hair: z.enum([
      'short',
      'side-part',
      'swept',
      'bald',
      'balding',
      'buzz',
      'bob',
      'long',
      'ponytail',
      'bun',
      'curly',
      'quiff',
      'bouffant',
    ]),
    facialHair: z.enum(['none', 'moustache', 'beard', 'stubble', 'goatee']),
    glasses: z.enum(['none', 'round', 'square', 'shades']),
    headwear: z.enum([
      'none',
      'cap',
      'plume',
      'hardhat',
      'gele',
      'fila',
      'headwrap',
      'beret',
      'visor',
    ]),
    neck: z.enum(['tie', 'bowtie', 'scarf', 'pearls', 'open', 'mandarin', 'pet-collar']),
    earpiece: z.boolean(),
  }),
});
export type CharacterBuild = z.infer<typeof castMemberViewSchema>['build'];

export const sceneViewSchema = z.object({
  id: z.string(),
  title: z.string(),
  lines: z.array(
    z.object({
      /** Cast id, or `narrator`. */
      speakerId: z.string(),
      speaker: z.string(),
      expression: expressionSchema,
      text: z.string(),
    }),
  ),
});

/** The current decision, as your team sees it. The other team's options are never sent. */
export const episodeRoundViewSchema = z.object({
  sceneId: z.string(),
  stage: z.enum(['reading', 'choosing', 'outcome']),
  deadline: isoDate.nullable(),
  prompt: z.string(),
  options: z.array(z.object({ id: z.string(), label: z.string(), hint: z.string() })),
  yourChoice: z.string().nullable(),
  /** Your teammates' locked picks (team-private). */
  teamPicks: z.array(z.object({ slot: z.number().int(), choiceId: z.string() })),
});

export const roundOutcomeViewSchema = z.object({
  sceneId: z.string(),
  sceneTitle: z.string(),
  result: z.enum(['success', 'foiled', 'hesitated']),
  text: z.string(),
  pressureDelta: z.number().int(),
  coverDelta: z.number().int(),
  /** What your own team decided this round (label), if anything. */
  yourTeamChoice: z.string().nullable(),
  /**
   * What the other team decided (label), revealed only once the round is resolved: history
   * never contains a round still being played.
   */
  otherTeamChoice: z.string().nullable(),
});

export const endingViewSchema = z.object({
  winner: teamSchema,
  tone: z.enum(['ousted', 'cartoon-exit', 'survives']),
  headline: z.string(),
  text: z.string(),
  youWon: z.boolean().nullable(),
});

export const episodeViewSchema = z.object({
  id: z.string(),
  title: z.string(),
  premise: z.string(),
  /** Your team's briefing only. */
  objective: z.string().nullable(),
  cast: z.array(castMemberViewSchema),
  scene: sceneViewSchema.nullable(),
  round: episodeRoundViewSchema.nullable(),
  roundNumber: z.number().int().min(0),
  totalRounds: z.number().int().min(0),
  meters: z.object({ pressure: z.number().int(), cover: z.number().int() }),
  history: z.array(roundOutcomeViewSchema),
  ending: endingViewSchema.nullable(),
});

export const submitChoiceRequestSchema = z
  .object({
    sceneId: z.string().regex(/^[a-z][a-z0-9-]{1,39}$/),
    choiceId: z.string().regex(/^[a-z][a-z0-9-]{1,39}$/),
    requestId: z.uuid(),
  })
  .strict();
export type SubmitChoiceRequest = z.infer<typeof submitChoiceRequestSchema>;

export const matchViewSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  visibility: lobbyVisibilitySchema,
  maxPlayers: maxPlayersSchema,
  phase: matchPhaseSchema,
  phaseDeadline: isoDate.nullable(),
  serverNow: isoDate,
  /** Public lobbies: how long the table waits for people before empty seats start to fill. */
  lobbyWaitSeconds: z.number().int().positive().nullable(),
  you: z.object({ slot: z.number().int(), team: teamSchema.nullable(), isHost: z.boolean() }),
  players: z.array(playerViewSchema),
  vote: voteRoundViewSchema.nullable(),
  resolution: resolutionViewSchema.nullable(),
  episode: episodeViewSchema.nullable(),
});

export type MatchPhaseName = z.infer<typeof matchPhaseSchema>;
export type PlayerView = z.infer<typeof playerViewSchema>;
export type VoteRoundView = z.infer<typeof voteRoundViewSchema>;
export type ResolutionView = z.infer<typeof resolutionViewSchema>;
export type EpisodeView = z.infer<typeof episodeViewSchema>;
export type CastMemberView = z.infer<typeof castMemberViewSchema>;
export type SceneView = z.infer<typeof sceneViewSchema>;
export type EpisodeRoundView = z.infer<typeof episodeRoundViewSchema>;
export type RoundOutcomeView = z.infer<typeof roundOutcomeViewSchema>;
export type EndingView = z.infer<typeof endingViewSchema>;
export type MatchView = z.infer<typeof matchViewSchema>;

export const currentMatchResponseSchema = z.object({ match: matchViewSchema.nullable() });

export const voteAcceptedResponseSchema = z.object({
  accepted: z.literal(true),
  roundId: z.uuid(),
  countryId: countryIdSchema,
});

// ---- Realtime (server → client only; actions go over HTTP with CSRF) ----

export const serverMessageSchema = z.discriminatedUnion('type', [
  z.object({ v: z.literal(1), type: z.literal('hello'), serverNow: isoDate }),
  z.object({ v: z.literal(1), type: z.literal('match.state'), match: matchViewSchema.nullable() }),
]);
export type ServerMessage = z.infer<typeof serverMessageSchema>;

/** The client may only ping; anything else closes the socket. */
export const clientMessageSchema = z.object({ v: z.literal(1), type: z.literal('ping') }).strict();

export const CLOSE_CODES = {
  SESSION_ENDED: 4401,
  FORBIDDEN: 4403,
  PROTOCOL_MISMATCH: 4426,
  POLICY_VIOLATION: 4408,
} as const;
